import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorIcon from '@mui/icons-material/Error';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PublishIcon from '@mui/icons-material/Publish';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import StatCard from '../../lib/components/StatCard';
import {
  canPromoteToLive,
  compareScenarios,
  describeModification,
  estimateRunTime,
  rankScenarios,
} from '../../lib/utils/simulationHelpers';
import { useSimulationStore } from '../../stores/simulationStore';
import type { SimulationModification, SimulationScenario } from '../../types';
import { entityTypeLabels, getSimulationStats, modificationTypeLabels, statusConfig } from './MockData';

// Tab panel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const Simulations: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newScenarioName, setNewScenarioName] = useState('');
  const [newScenarioDescription, setNewScenarioDescription] = useState('');
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateSourceId, setDuplicateSourceId] = useState<string | null>(null);
  const [duplicateName, setDuplicateName] = useState('');

  // Store
  const {
    scenarios,
    activeScenario,
    comparisonScenarios,
    isRunning,
    runProgress,
    setActiveScenario,
    createScenario,
    deleteScenario,
    duplicateScenario,
    runSimulation,
    addToComparison,
    removeFromComparison,
    clearComparison,
    promoteToLive,
  } = useSimulationStore();

  // Stats
  const stats = useMemo(() => getSimulationStats(scenarios), [scenarios]);

  // Ranked scenarios for comparison
  const rankedScenarios = useMemo(() => rankScenarios(scenarios), [scenarios]);

  // Comparison results
  const comparisonResults = useMemo(() => {
    if (comparisonScenarios.length < 2) return null;

    const [id1, id2] = comparisonScenarios;
    const s1 = scenarios.find((s) => s.id === id1);
    const s2 = scenarios.find((s) => s.id === id2);

    if (!s1 || !s2) return null;

    return compareScenarios(s1, s2);
  }, [comparisonScenarios, scenarios]);

  // Handlers
  const handleTabChange = useCallback((_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  }, []);

  const handleCreateScenario = useCallback(() => {
    if (!newScenarioName.trim()) return;

    createScenario({
      name: newScenarioName.trim(),
      description: newScenarioDescription.trim(),
      baseTimetableId: 'timetable-current',
      modifications: [],
    });

    setNewScenarioName('');
    setNewScenarioDescription('');
    setCreateDialogOpen(false);
  }, [newScenarioName, newScenarioDescription, createScenario]);

  const handleDuplicate = useCallback(() => {
    if (!duplicateSourceId || !duplicateName.trim()) return;

    duplicateScenario(duplicateSourceId, duplicateName.trim());
    setDuplicateSourceId(null);
    setDuplicateName('');
    setDuplicateDialogOpen(false);
  }, [duplicateSourceId, duplicateName, duplicateScenario]);

  const openDuplicateDialog = useCallback((scenarioId: string, currentName: string) => {
    setDuplicateSourceId(scenarioId);
    setDuplicateName(`${currentName} (Copy)`);
    setDuplicateDialogOpen(true);
  }, []);

  const handleRunScenario = useCallback(
    (scenarioId: string) => {
      runSimulation(scenarioId);
    },
    [runSimulation],
  );

  const handlePromote = useCallback(
    (scenarioId: string) => {
      const scenario = scenarios.find((s) => s.id === scenarioId);
      if (!scenario) return;

      const { canPromote, reasons } = canPromoteToLive(scenario);
      if (!canPromote) {
        alert(`Cannot promote: ${reasons.join(', ')}`);
        return;
      }

      if (window.confirm('Are you sure you want to promote this scenario to live? This will update the main timetable.')) {
        promoteToLive(scenarioId);
      }
    },
    [scenarios, promoteToLive],
  );

  const toggleComparison = useCallback(
    (scenarioId: string) => {
      if (comparisonScenarios.includes(scenarioId)) {
        removeFromComparison(scenarioId);
      } else {
        addToComparison(scenarioId);
      }
    },
    [comparisonScenarios, addToComparison, removeFromComparison],
  );

  // Render scenario card
  const renderScenarioCard = (scenario: SimulationScenario) => {
    const config = statusConfig[scenario.status];
    const isSelected = comparisonScenarios.includes(scenario.id);
    const estimatedTime = estimateRunTime(scenario.modifications);
    const promotionCheck = canPromoteToLive(scenario);

    return (
      <Card
        key={scenario.id}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          border: isSelected ? '2px solid' : '1px solid',
          borderColor: isSelected ? 'primary.main' : 'divider',
        }}
      >
        <CardContent sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="h6" noWrap sx={{ flex: 1, mr: 1 }}>
              {scenario.name}
            </Typography>
            <Chip label={config.label} color={config.color} size="small" />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
            {scenario.description}
          </Typography>

          {/* Modifications summary */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {scenario.modifications.length} modification(s)
            </Typography>
            {scenario.modifications.slice(0, 2).map((mod, index) => (
              <Typography key={index} variant="caption" display="block" sx={{ ml: 1 }}>
                • {describeModification(mod)}
              </Typography>
            ))}
            {scenario.modifications.length > 2 && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ... and {scenario.modifications.length - 2} more
              </Typography>
            )}
          </Box>

          {/* Results summary if completed */}
          {scenario.results && (
            <Box sx={{ mt: 'auto' }}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {scenario.results.feasible ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : (
                  <ErrorIcon color="error" fontSize="small" />
                )}
                <Typography variant="body2">{scenario.results.feasible ? 'Feasible' : 'Not Feasible'}</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Conflicts: {scenario.results.stats.conflictCount} | Coverage:{' '}
                {scenario.results.stats.curriculumCoverage.toFixed(1)}%
              </Typography>
            </Box>
          )}

          {/* Running progress */}
          {scenario.status === 'running' && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={runProgress} />
              <Typography variant="caption" color="text.secondary">
                Running... {runProgress}%
              </Typography>
            </Box>
          )}
        </CardContent>

        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Box>
            <Tooltip title={isSelected ? 'Remove from comparison' : 'Add to comparison'}>
              <IconButton
                size="small"
                onClick={() => toggleComparison(scenario.id)}
                color={isSelected ? 'primary' : 'default'}
                disabled={scenario.status !== 'completed'}
              >
                <CompareArrowsIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Duplicate">
              <IconButton size="small" onClick={() => openDuplicateDialog(scenario.id, scenario.name)}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  if (window.confirm('Delete this scenario?')) {
                    deleteScenario(scenario.id);
                  }
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {scenario.status === 'draft' && (
              <Tooltip title={`Run (${estimatedTime.label})`}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => handleRunScenario(scenario.id)}
                  disabled={isRunning}
                >
                  Run
                </Button>
              </Tooltip>
            )}
            {scenario.status === 'completed' && promotionCheck.canPromote && (
              <Tooltip title="Promote to live timetable">
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  startIcon={<PublishIcon />}
                  onClick={() => handlePromote(scenario.id)}
                >
                  Promote
                </Button>
              </Tooltip>
            )}
          </Box>
        </CardActions>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            What-If Simulations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Test hypothetical changes without affecting the live timetable
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}>
          New Simulation
        </Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <StatCard stat={{ label: 'Total', value: stats.total, color: 'info' }} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <StatCard stat={{ label: 'Completed', value: stats.completed, color: 'success' }} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <StatCard stat={{ label: 'Running', value: stats.running, color: 'warning' }} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <StatCard stat={{ label: 'Draft', value: stats.draft }} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <StatCard stat={{ label: 'Failed', value: stats.failed, color: 'error' }} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <StatCard stat={{ label: 'Feasible Rate', value: `${stats.feasibleRate.toFixed(0)}%`, color: 'success' }} />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="All Scenarios" />
          <Tab label={`Compare (${comparisonScenarios.length})`} disabled={comparisonScenarios.length < 2} />
          <Tab label="Rankings" />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        {scenarios.length === 0 ? (
          <Alert severity="info">
            No simulations yet. Create your first what-if scenario to test hypothetical changes.
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {scenarios.map((scenario) => (
              <Grid item xs={12} sm={6} lg={4} key={scenario.id}>
                {renderScenarioCard(scenario)}
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {comparisonResults ? (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Comparison: {comparisonResults.scenario1.name} vs {comparisonResults.scenario2.name}
            </Typography>

            <Alert severity={comparisonResults.winner === 'tie' ? 'info' : 'success'} sx={{ mb: 3 }}>
              {comparisonResults.summary}
            </Alert>

            <Grid container spacing={3}>
              {/* Scenario 1 */}
              <Grid item xs={12} md={6}>
                <Card sx={{ border: comparisonResults.winner === 'scenario1' ? '2px solid' : '1px solid', borderColor: comparisonResults.winner === 'scenario1' ? 'success.main' : 'divider' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {comparisonResults.scenario1.name}
                      {comparisonResults.winner === 'scenario1' && (
                        <Chip label="Winner" color="success" size="small" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    <Typography variant="body2">
                      Conflicts: {comparisonResults.scenario1.results?.stats.conflictCount || 0}
                    </Typography>
                    <Typography variant="body2">
                      Coverage: {comparisonResults.scenario1.results?.stats.curriculumCoverage.toFixed(1) || 0}%
                    </Typography>
                    <Typography variant="body2">
                      Teacher Satisfaction: {comparisonResults.scenario1.results?.stats.teacherPreferenceSatisfaction.toFixed(0) || 0}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Scenario 2 */}
              <Grid item xs={12} md={6}>
                <Card sx={{ border: comparisonResults.winner === 'scenario2' ? '2px solid' : '1px solid', borderColor: comparisonResults.winner === 'scenario2' ? 'success.main' : 'divider' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {comparisonResults.scenario2.name}
                      {comparisonResults.winner === 'scenario2' && (
                        <Chip label="Winner" color="success" size="small" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    <Typography variant="body2">
                      Conflicts: {comparisonResults.scenario2.results?.stats.conflictCount || 0}
                    </Typography>
                    <Typography variant="body2">
                      Coverage: {comparisonResults.scenario2.results?.stats.curriculumCoverage.toFixed(1) || 0}%
                    </Typography>
                    <Typography variant="body2">
                      Teacher Satisfaction: {comparisonResults.scenario2.results?.stats.teacherPreferenceSatisfaction.toFixed(0) || 0}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={clearComparison}>
                Clear Comparison
              </Button>
            </Box>
          </Paper>
        ) : (
          <Alert severity="info">
            Select at least 2 completed scenarios to compare. Click the compare icon on scenario cards.
          </Alert>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {rankedScenarios.length === 0 ? (
          <Alert severity="info">No completed scenarios to rank yet.</Alert>
        ) : (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Scenario Rankings
            </Typography>
            {rankedScenarios.map((ranked) => (
              <Card key={ranked.scenario.id} sx={{ mb: 2, border: ranked.rank === 1 ? '2px solid' : '1px solid', borderColor: ranked.rank === 1 ? 'success.main' : 'divider' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Chip label={`#${ranked.rank}`} color={ranked.rank === 1 ? 'success' : 'default'} />
                    <Typography variant="h6">{ranked.scenario.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Score: {ranked.score.toFixed(1)}
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="success.main" fontWeight="bold">
                        Strengths:
                      </Typography>
                      {ranked.strengths.map((s, i) => (
                        <Typography key={i} variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CheckCircleIcon color="success" fontSize="small" />
                          {s}
                        </Typography>
                      ))}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="error.main" fontWeight="bold">
                        Weaknesses:
                      </Typography>
                      {ranked.weaknesses.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          None identified
                        </Typography>
                      ) : (
                        ranked.weaknesses.map((w, i) => (
                          <Typography key={i} variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <WarningIcon color="warning" fontSize="small" />
                            {w}
                          </Typography>
                        ))
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Paper>
        )}
      </TabPanel>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Simulation</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Scenario Name"
            fullWidth
            value={newScenarioName}
            onChange={(e) => setNewScenarioName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={newScenarioDescription}
            onChange={(e) => setNewScenarioDescription(e.target.value)}
            placeholder="Describe what changes you want to test..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateScenario} disabled={!newScenarioName.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Dialog */}
      <Dialog open={duplicateDialogOpen} onClose={() => setDuplicateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Duplicate Scenario</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Scenario Name"
            fullWidth
            value={duplicateName}
            onChange={(e) => setDuplicateName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleDuplicate} disabled={!duplicateName.trim()}>
            Duplicate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Simulations;

